/**
 * useCapacitorPlugins — wraps all @capacitor/* plugin calls behind
 * safe-import guards so the same bundle runs in the browser (where
 * Capacitor plugins silently no-op) and inside the native app.
 *
 * Usage:
 *   const cap = useCapacitorPlugins();
 *   cap.haptics.impact('light');
 *   cap.haptics.selection();
 *   cap.keyboard.dismiss();
 *   cap.statusBar.setLight();
 *   cap.statusBar.setColor('#080a0f');
 *
 * All methods are synchronous fire-and-forget. Errors are silently
 * swallowed — plugin failures must never crash the UI.
 */
import { useEffect, useRef } from 'react';

// ── Safe dynamic plugin loader ─────────────────────────────────────
// We load these lazily so the browser bundle doesn't fail on missing
// native bindings. In a proper Capacitor build all packages are
// present; in a browser the import still resolves but calls are no-ops.
let _haptics = null;
let _keyboard = null;
let _statusBar = null;
let _appPlugin = null;

async function loadPlugins() {
  try {
    if (!_haptics) {
      const { Haptics, ImpactStyle, NotificationType } = await import('@capacitor/haptics');
      _haptics = { Haptics, ImpactStyle, NotificationType };
    }
  } catch { /* not available in browser */ }

  try {
    if (!_keyboard) {
      const { Keyboard } = await import('@capacitor/keyboard');
      _keyboard = { Keyboard };
    }
  } catch {}

  try {
    if (!_statusBar) {
      const { StatusBar, Style } = await import('@capacitor/status-bar');
      _statusBar = { StatusBar, Style };
    }
  } catch {}

  try {
    if (!_appPlugin) {
      const { App } = await import('@capacitor/app');
      _appPlugin = { App };
    }
  } catch {}
}

// Only load in Capacitor native context
const IS_CAPACITOR =
  !!(window.Capacitor?.isNativePlatform?.()) ||
  !!window.Capacitor?.platform;

if (IS_CAPACITOR) {
  loadPlugins();
}

// ── Haptics helper ─────────────────────────────────────────────────
const haptics = {
  /** Light tap — use on every navigation tap */
  impact(style = 'light') {
    if (!_haptics) return;
    const map = {
      light:  _haptics.ImpactStyle.Light,
      medium: _haptics.ImpactStyle.Medium,
      heavy:  _haptics.ImpactStyle.Heavy,
    };
    _haptics.Haptics.impact({ style: map[style] ?? _haptics.ImpactStyle.Light })
      .catch(() => {});
  },
  /** Short vibration for selection feedback */
  selection() {
    if (!_haptics) return;
    _haptics.Haptics.selectionChanged().catch(() => {});
  },
  /** Success / error / warning pattern */
  notify(type = 'SUCCESS') {
    if (!_haptics) return;
    const map = {
      SUCCESS: _haptics.NotificationType.Success,
      ERROR:   _haptics.NotificationType.Error,
      WARNING: _haptics.NotificationType.Warning,
    };
    _haptics.Haptics.notification({ type: map[type] ?? _haptics.NotificationType.Success })
      .catch(() => {});
  },
};

// ── Keyboard helper ────────────────────────────────────────────────
const keyboard = {
  dismiss() {
    _keyboard?.Keyboard?.hide().catch(() => {});
  },
  setAccessory(show) {
    _keyboard?.Keyboard?.setAccessoryBarVisible({ isVisible: show }).catch(() => {});
  },
};

// 2026-10-13 — full viewport height unit for --mobile-h. Old Android
// System WebViews (< Chrome 108) don't know dvh; writing calc(100dvh - …)
// there makes every height that reads --mobile-h invalid (panes collapse).
const VIEWPORT_H = (typeof CSS !== 'undefined' && CSS.supports && CSS.supports('height', '100dvh')) ? '100dvh' : '100vh';

// ── StatusBar helper ───────────────────────────────────────────────
const statusBar = {
  /**
   * Status bar text to suit the current theme. Capacitor names the style
   * after the BACKGROUND (see @capacitor/status-bar's definitions.d.ts):
   *   Style.Dark  = LIGHT text, for dark backgrounds  → Midnight theme
   *   Style.Light = DARK text, for light backgrounds  → Arena (default), Zen
   * 2026-10-09 — this used to set Style.Light everywhere under the opposite
   * reading, which left dark text on the dark Midnight theme.
   */
  syncToTheme() {
    if (!IS_CAPACITOR) return;
    loadPlugins().then(() => {
      if (!_statusBar) return;
      const dark = document.documentElement.getAttribute('data-theme') === 'midnight';
      _statusBar.StatusBar.setStyle({ style: dark ? _statusBar.Style.Dark : _statusBar.Style.Light }).catch(() => {});
    });
  },
  /** Dark status bar text, for light backgrounds. Prefer syncToTheme(). */
  setLight() {
    if (!_statusBar) return;
    _statusBar.StatusBar.setStyle({ style: _statusBar.Style.Light }).catch(() => {});
  },
  /** Set background color (Android only; iOS ignores this) */
  setColor(color) {
    if (!_statusBar) return;
    _statusBar.StatusBar.setBackgroundColor({ color }).catch(() => {});
  },
  /** Overlay the web view under the status bar (the default) */
  setOverlay(overlay = true) {
    if (!_statusBar) return;
    _statusBar.StatusBar.setOverlaysWebView({ overlay }).catch(() => {});
  },
};

// ── React hook ────────────────────────────────────────────────────
export function useCapacitorPlugins({ onBackButton } = {}) {
  const backRef = useRef(onBackButton);
  backRef.current = onBackButton;

  // Configure StatusBar once on mount
  useEffect(() => {
    if (!IS_CAPACITOR) return;

    loadPlugins().then(() => {
      statusBar.syncToTheme();   // dark or light text to suit the theme
      statusBar.setColor('#080a0f');
      statusBar.setOverlay(true);
    });
  }, []);

  // Android hardware back button
  useEffect(() => {
    if (!IS_CAPACITOR) return;
    let mounted = true;
    let listenerHandle = null; // store the resolved handle for cleanup

    loadPlugins().then(() => {
      if (!mounted || !_appPlugin) return;
      // addListener returns a Promise<PluginListenerHandle>
      _appPlugin.App.addListener('backButton', ({ canGoBack }) => {
        if (typeof backRef.current === 'function') {
          backRef.current({ canGoBack });
        } else if (canGoBack) {
          window.history.back();
        } else {
          _appPlugin.App.minimizeApp().catch(() => {});
        }
      }).then(handle => {
        if (!mounted) {
          // Component already unmounted before promise resolved — remove immediately
          handle.remove();
        } else {
          listenerHandle = handle;
        }
      }).catch(() => {});
    });

    return () => {
      mounted = false;
      listenerHandle?.remove();
    };
  }, []);

  // Keyboard show/hide — adjust --keyboard-h CSS variable so layouts can react
  useEffect(() => {
    if (!IS_CAPACITOR) return;
    let mounted = true;
    const handles = []; // store resolved handles for cleanup

    loadPlugins().then(() => {
      if (!mounted || !_keyboard) return;

      // 2026-09-16 — remember the WebView height from BEFORE the keyboard
      // so keyboardDidShow can tell whether the WebView itself was resized
      // (see below).
      let innerBeforeKeyboard = 0;
      Promise.all([
        _keyboard.Keyboard.addListener('keyboardWillShow', info => {
          innerBeforeKeyboard = window.innerHeight;
          document.documentElement.style.setProperty('--keyboard-h', `${info.keyboardHeight}px`);
          document.documentElement.classList.add('keyboard-open');
          // 2026-07-14 (mobile pass) — CapacitorLayout never set --mobile-h
          // (only MobileLayout/TabletLayout did, via visualViewport.height,
          // which already shrinks when a web keyboard opens). A whole family
          // of components read `var(--mobile-h, 100dvh)` to size themselves
          // to "the currently visible area" — ChatPage's composer, the
          // DashboardPage/SchedulePage/GamePage modals, GameScoreboardPage —
          // and in the Capacitor app that variable was always undefined, so
          // every one of them fell back to the static `100dvh`, which does
          // NOT shrink for the native keyboard. Net effect inside the
          // Capacitor wrapper: the keyboard opens, covers the bottom of a
          // container that never got shorter, and the composer / modal
          // footer ends up hidden behind the keyboard — the reported "chat
          // cut off" bug. Deriving --mobile-h here (100dvh minus the native
          // keyboard height reported by the plugin) makes those same
          // consumers shrink correctly without touching each of them.
          document.documentElement.style.setProperty('--mobile-h', `calc(${VIEWPORT_H} - ${info.keyboardHeight}px)`);
        }),
        _keyboard.Keyboard.addListener('keyboardWillHide', () => {
          document.documentElement.style.setProperty('--keyboard-h', '0px');
          document.documentElement.classList.remove('keyboard-open');
          document.documentElement.style.setProperty('--mobile-h', VIEWPORT_H);
        }),
        // 2026-09-16 (mobile "message box hidden" report) — guard against
        // subtracting the keyboard TWICE. The willShow handler above
        // assumes the WebView keeps its full height and the keyboard
        // overlays it (iOS with Keyboard.resize = 'body'). On Android
        // (`resizeOnFullScreen: true`, or a manifest with adjustResize)
        // the WebView itself is shrunk to sit above the keyboard, so
        // `100dvh` ALREADY excludes it; `calc(100dvh - kb)` then made every
        // --mobile-h consumer (these modals, the chat composer) a full
        // keyboard-height too short — the sheet ended well above the
        // keyboard with its bottom (where the textarea lives) clipped
        // away — and lifting the tab bar by --keyboard-h pushed it into
        // the middle of the screen. Measure instead of assuming: if the
        // WebView lost at least half the keyboard's height between
        // willShow and didShow, it was resized natively → keep 100dvh and
        // don't lift the tab bar.
        _keyboard.Keyboard.addListener('keyboardDidShow', info => {
          const shrank = innerBeforeKeyboard - window.innerHeight;
          if (innerBeforeKeyboard > 0 && shrank >= (info.keyboardHeight || 0) * 0.5) {
            document.documentElement.style.setProperty('--mobile-h', VIEWPORT_H);
            document.documentElement.style.setProperty('--keyboard-h', '0px');
          }
        }),
      ]).then(([h1, h2, h3]) => {
        if (!mounted) {
          // Unmounted before the listeners resolved — clean up immediately
          h1.remove(); h2.remove(); h3.remove();
        } else {
          handles.push(h1, h2, h3);
        }
      }).catch(() => {});
    });

    return () => {
      mounted = false;
      handles.forEach(h => h.remove());
    };
  }, []);

  return {
    isCapacitor: IS_CAPACITOR,
    haptics,
    keyboard,
    statusBar,
  };
}

// Standalone helpers (no hook needed, safe anywhere)
export { haptics, keyboard, statusBar, IS_CAPACITOR };
